<!-- The frame of the small "card" dialogs the legacy code created on the fly (Manage projects, Plan, First project,
     What's new): lighter overlay with p-4, a rounded-3xl panel with the opening animation (.modal-animate). Two shapes:
     - with the `title` slot: header (icon + title in the slot, × on the right), then the scrollable body (default slot,
       `bodyClass`) in a panel of at most 90% of the screen height (Manage projects, What's new);
     - without it: the default slot is the whole panel content, with p-6 or `panelClass` (Plan, First project, the
       service choice). With `heading` (icon tile from `icon`, title, `subtitle`) or the `header` slot (Icon and colour,
       whose title has its own chip) a header row with × on the right comes first (Shorts, Calendar on your phone).
     Teleported to <body>; registers itself in the dialog stack (src/stores/dialogs.ts) like AppDialog.vue, whose
     frame is the edit-dialog one. It never closes itself: it emits "close" (×, outside click with `outside`, Esc with
     `esc` through the dialog stack) and the owner hides it. Recipe: docs/migration.md, "Dialogs and fields". -->
<script setup lang="ts">
import { useDialogEntry } from '../composables/useDialogEntry'
import { dialogWidth } from '../utils/dialogWidth'
import { useI18n } from '../composables/useI18n'

const props = withDefaults(defineProps<{
  /** Dialog name in the stack (isDialogOpen) */
  name: string
  /** id of the overlay (keep the legacy one) */
  id?: string
  /** Panel width class, written whole (wider on large screens: dialogWidth): 'max-w-md' (default), 'max-w-lg' */
  width?: string
  /** z-index class, written whole: 'z-50' (default), 'z-[93]' (first project) */
  z?: string
  /** Overlay colour class, written whole: 'bg-black/70' (default), 'bg-black/80' */
  dim?: string
  /** A click on the overlay closes it */
  outside?: boolean
  /** Esc closes it (dialog stack) */
  esc?: boolean
  /** The user types data in it: the online sync waits until it closes */
  editing?: boolean
  /** Body classes of the shape with a title */
  bodyClass?: string
  /** Padding and scrolling of the panel without a title, written whole */
  panelClass?: string
  /** Header of the panel without a title: Font Awesome icon of the tile ('fa-scissors'), title and subtitle */
  icon?: string
  heading?: string
  subtitle?: string
}>(), {
  id: undefined,
  width: 'max-w-md',
  z: 'z-50',
  dim: 'bg-black/70',
  outside: false,
  esc: false,
  editing: false,
  bodyClass: 'p-5 overflow-y-auto space-y-3',
  panelClass: 'p-6 max-h-[90dvh] overflow-y-auto',
  icon: '',
  heading: '',
  subtitle: ''
})
const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
const { onDown, onClick } = useDialogEntry({ name: props.name, esc: props.esc, editing: props.editing }, () => emit('close'), () => props.outside)
</script>

<template>
  <Teleport to="body">
    <div :id="id" :class="['fixed inset-0 backdrop-blur-sm flex items-center justify-center p-4', z, dim]" @mousedown="onDown" @click="onClick">
      <div v-if="$slots.title" :class="['bg-darkcard border border-gray-800 w-full rounded-3xl shadow-2xl flex flex-col max-h-[90dvh] modal-animate', dialogWidth(width)]">
        <div class="p-5 border-b border-gray-800 flex items-center justify-between"><h3 class="text-lg font-black text-gray-100"><slot name="title" /></h3>
          <button type="button" :title="t('Close')" :aria-label="t('Close')" class="text-gray-400 hover:text-gray-100" @click="emit('close')"><i class="fas fa-xmark text-lg"></i></button></div>
        <div :class="bodyClass"><slot /></div>
      </div>
      <div v-else :class="['bg-darkcard border border-gray-800 w-full rounded-3xl shadow-2xl modal-animate', panelClass, dialogWidth(width)]">
        <div v-if="heading || $slots.header" class="flex items-center gap-3 mb-4">
          <slot name="header">
            <span class="w-10 h-10 rounded-2xl bg-brand/15 text-brand flex items-center justify-center"><i :class="['fas', icon]"></i></span>
            <div class="flex-1"><h3 class="font-black text-gray-100">{{ heading }}</h3><p v-if="subtitle" class="text-[11px] text-gray-400">{{ subtitle }}</p></div>
          </slot>
          <button type="button" :title="t('Close')" :aria-label="t('Close')" class="text-gray-500 hover:text-gray-100" @click="emit('close')"><i class="fas fa-xmark"></i></button>
        </div>
        <slot />
      </div>
    </div>
  </Teleport>
</template>
